import { RiskFactorDto, RiskFactorPerCancerDto, RiskFactorToCancersImpactDto } from '@/api/generated/model';
import { formatRiskDecimal } from '@/utils/cancer/risk/format-risk-decimal';
import { isNil } from '@/utils/common/is-nil';
import { Box, Button, Flex, Grid, IconArrowDown, IconArrowUp, Text } from '@cmpsr/components';
import React from 'react';

export const RiskFactorBreakdownTableByFactor = ({
  cancer,
  onRiskFactorClick,
}: {
  cancer: RiskFactorPerCancerDto;
  onRiskFactorClick: (riskFactor: RiskFactorToCancersImpactDto) => void;
}) => {
  const yourBehaviorText = (riskFactor: RiskFactorDto) => {
    switch (riskFactor.riskFactorType) {
      case 'regular':
        return isNil(riskFactor.yourValue) ? null : (
          <Text variant="text-body-floating-label-regular" color="text-primary">
            <Text as="span" color="text-tertiary" variant="text-body-floating-label-regular">
              You
            </Text>{' '}
            {riskFactor.yourValue}
          </Text>
        );
      case 'non-quantifiable-positive':
      case 'non-quantifiable-negative': {
        return (
          <Text variant="text-body-floating-label-regular" color="text-primary">
            {riskFactor.shortDescription}
          </Text>
        );
      }
    }
  };

  const populationAverageText = (riskFactor: RiskFactorDto) => {
    switch (riskFactor.riskFactorType) {
      case 'regular':
        return (
          <Text variant="text-body-floating-label-regular" color="text-primary">
            {riskFactor.populationAverage}
          </Text>
        );
      case 'non-quantifiable-positive':
      case 'non-quantifiable-negative': {
        return null;
      }
    }
  };

  const impactText = (riskFactor: RiskFactorDto) => {
    switch (riskFactor.riskFactorType) {
      case 'regular':
        return (
          <Box
            backgroundColor={riskFactor.impact < 0 ? 'alert-success-default' : 'alert-error-default'}
            px={2}
            py={1}
            borderRadius="radii-2"
            color={riskFactor.impact < 0 ? 'black' : 'white'}
            minWidth={'70px'}
            textAlign="center"
          >
            <Text variant="text-body-floating-label-bold" color="text-primary">
              {riskFactor.impact < 0 ? <IconArrowDown size="xs" /> : <IconArrowUp size="xs" />}{' '}
              {formatRiskDecimal(Math.abs(riskFactor.impact))}
            </Text>
          </Box>
        );
      case 'non-quantifiable-positive': {
        return (
          <Box backgroundColor="alert-success-default" px={2} py={1} borderRadius="radii-2">
            <Text variant="text-body-floating-label-bold" color="text-primary" width={10} textAlign="center">
              <IconArrowDown size="xs" />
            </Text>
          </Box>
        );
      }
      case 'non-quantifiable-negative': {
        return (
          <Box backgroundColor="alert-error-default" px={2} py={1} borderRadius="radii-2">
            <Text variant="text-body-floating-label-bold" color="text-primary" width={10} textAlign="center">
              <IconArrowUp size="xs" />
            </Text>
          </Box>
        );
      }
    }
  };

  const stylesTableItem = (index?: number, arr?: RiskFactorDto[]) => {
    const isLast = index !== undefined && arr !== undefined && index === arr.length - 1;
    return {
      // Do not show a border on the last row of items in the table.
      // _last/last-child won't work here because of the grid layout.
      borderBottom: isLast ? 'none' : '1px solid',
      borderColor: 'ui-element-divider',
      p: 3,
    };
  };

  return (
    <Grid templateColumns="1fr 1fr 1fr" gap={0} mt={3}>
      <Text variant="text-body-floating-label-regular" {...stylesTableItem()}>
        Risk Factor
      </Text>
      <Text variant="text-body-floating-label-regular" textAlign="start" {...stylesTableItem()}>
        Population Average
      </Text>
      <Text variant="text-body-floating-label-regular" textAlign="end" {...stylesTableItem()}>
        Impact
      </Text>

      {cancer.riskFactors?.map((factor, index, arr) => (
        <React.Fragment key={`${factor.id || index}`}>
          <Flex direction="column" justifyContent="flex-end" {...stylesTableItem(index, arr)}>
            <Button
              variant="link"
              size="xs"
              justifyContent="flex-start"
              mb={1}
              textAlign="left"
              fontWeight="600"
              whiteSpace="normal"
              textDecoration={'underline'}
              _hover={{ textDecoration: 'none' }}
              onClick={() => onRiskFactorClick(factor)}
            >
              {factor.label}
            </Button>
            {yourBehaviorText(factor)}
          </Flex>
          <Flex direction="column" alignItems="flex-start" justifyContent="center" {...stylesTableItem(index, arr)}>
            {populationAverageText(factor)}
          </Flex>
          <Flex alignItems="center" justifyContent="flex-end" {...stylesTableItem(index, arr)}>
            {impactText(factor)}
          </Flex>
        </React.Fragment>
      ))}

      {cancer.riskFactors.length === 0 && (
        <Box gridColumn="span 3" p={4} textAlign="center" color="gray.500">
          No risk factors found for this cancer type.
        </Box>
      )}
    </Grid>
  );
};
